import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Save,
  Shield,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  useCreateRol,
  useDeleteRol,
  usePermisos,
  useRoles,
  useSyncUsuarioPermisos,
  useSyncUsuarioRoles,
  useUpdateRol,
  useUsuariosRoles,
  settingsKeys,
  type Rol,
  type UsuarioConRoles,
} from "../api";

type Tab = "roles" | "usuarios" | "permisos";

export function RolesPermisosPage() {
  const queryClient = useQueryClient();
  const { data: grupos, isLoading: loadingPermissions, isFetching: fetchingPermissions } = usePermisos();
  const { data: roles, isLoading: loadingRoles, isFetching: fetchingRoles } = useRoles();
  const { data: usuarios, isLoading: loadingUsers, isFetching: fetchingUsers } = useUsuariosRoles();
  const createMut = useCreateRol();
  const updateMut = useUpdateRol();
  const deleteMut = useDeleteRol();
  const syncUserRolesMut = useSyncUsuarioRoles();
  const syncUserPermissionsMut = useSyncUsuarioPermisos();

  const [tab, setTab] = useState<Tab>("roles");
  const [editing, setEditing] = useState<Rol | null>(null);
  const [creating, setCreating] = useState(false);
  const [nombre, setNombre] = useState("");
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [permissionUser, setPermissionUser] = useState<UsuarioConRoles | null>(null);
  const [directPermissions, setDirectPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const totalPermisos = useMemo(
    () => (grupos ?? []).reduce((acc, grupo) => acc + grupo.permisos.length, 0),
    [grupos],
  );

  const loading = loadingPermissions || loadingRoles || loadingUsers;
  const refreshing = fetchingPermissions || fetchingRoles || fetchingUsers;

  async function refreshRecords() {
    setRefreshError(null);
    try {
      await queryClient.invalidateQueries({
        queryKey: settingsKeys.all,
        refetchType: "active",
      });
    } catch (requestError: unknown) {
      setRefreshError(resolveApiError(requestError, "No se pudieron actualizar los registros."));
    }
  }

  function openCreate() {
    setNombre("");
    setSeleccionados(new Set(["view-quality"]));
    setCreating(true);
    setEditing(null);
    setError(null);
  }

  function openEdit(role: Rol) {
    setNombre(role.name);
    setSeleccionados(new Set(role.permisos));
    setEditing(role);
    setCreating(false);
    setError(null);
  }

  function closeRoleModal() {
    setCreating(false);
    setEditing(null);
    setError(null);
  }

  function toggleRolePermission(name: string) {
    setSeleccionados((previous) => {
      const next = new Set(previous);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }

  function toggleRoleGroup(permisos: string[], allSelected: boolean) {
    setSeleccionados((previous) => {
      const next = new Set(previous);
      permisos.forEach((permission) => {
        allSelected ? next.delete(permission) : next.add(permission);
      });
      return next;
    });
  }

  async function saveRole(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      const permisos = Array.from(seleccionados);
      if (editing) {
        await updateMut.mutateAsync({
          id: editing.id,
          payload: { name: nombre, permisos },
        });
      } else {
        await createMut.mutateAsync({ name: nombre, permisos });
      }
      closeRoleModal();
    } catch (requestError: unknown) {
      setError(resolveApiError(requestError, "Error guardando el rol."));
    }
  }

  function openDirectPermissions(user: UsuarioConRoles) {
    setPermissionUser(user);
    setDirectPermissions(new Set(user.permisos_directos));
    setError(null);
  }

  function closeDirectPermissions() {
    setPermissionUser(null);
    setDirectPermissions(new Set());
    setError(null);
  }

  function toggleDirectPermission(name: string) {
    setDirectPermissions((previous) => {
      const next = new Set(previous);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }

  async function saveDirectPermissions(event: React.FormEvent) {
    event.preventDefault();
    if (!permissionUser) return;

    setError(null);
    try {
      await syncUserPermissionsMut.mutateAsync({
        id: permissionUser.id,
        permisos: Array.from(directPermissions),
      });
      closeDirectPermissions();
    } catch (requestError: unknown) {
      setError(resolveApiError(requestError, "No se pudieron guardar los permisos directos."));
    }
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Shield size={28} className="text-brand-700" /> Roles y permisos
          </h1>
          <p className="text-slate-500 mt-1">
            Define los permisos comunes de cada rol y las excepciones particulares de cada usuario.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => void refreshRecords()}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-soft transition hover:border-brand-300 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={17} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Actualizando..." : "Actualizar"}
          </button>
          {tab === "roles" && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
            >
              <Plus size={18} /> Nuevo rol
            </button>
          )}
        </div>
      </header>

      {refreshError ? <ErrorNotice message={refreshError} /> : null}

      <div className="flex flex-wrap gap-2 shrink-0">
        <TabButton active={tab === "roles"} onClick={() => setTab("roles")} icon={<Shield size={16} />}>
          Roles
        </TabButton>
        <TabButton active={tab === "usuarios"} onClick={() => setTab("usuarios")} icon={<Users size={16} />}>
          Asignar roles
        </TabButton>
        <TabButton active={tab === "permisos"} onClick={() => setTab("permisos")} icon={<KeyRound size={16} />}>
          Permisos por usuario
        </TabButton>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {tab === "roles" && roles && (
        <div className="flex-1 min-h-0 overflow-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((role) => (
            <div key={role.id} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-brand-100 text-brand-700 grid place-items-center">
                    <Shield size={18} />
                  </div>
                  <div>
                    <div className="font-black text-slate-900 flex items-center gap-1.5">
                      {role.name}
                      {role.protegido && <Lock size={12} className="text-slate-400" />}
                    </div>
                    <div className="text-xs text-slate-500">{role.users_count} usuarios</div>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-sm text-slate-600">
                <span className="font-bold text-slate-900">{role.permisos.length}</span> de {totalPermisos} permisos
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand-700 to-emerald-500"
                  style={{ width: `${totalPermisos ? (role.permisos.length / totalPermisos) * 100 : 0}%` }}
                />
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(role)}
                  disabled={role.protegido}
                  className="flex-1 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 text-sm font-bold text-slate-700"
                >
                  {role.protegido ? "Protegido" : "Editar permisos"}
                </button>
                {!role.protegido && (
                  <button
                    type="button"
                    onClick={() => {
                      if (role.users_count > 0) {
                        window.alert("No puedes eliminar un rol con usuarios asignados.");
                        return;
                      }
                      if (window.confirm(`¿Eliminar el rol "${role.name}"?`)) {
                        deleteMut.mutate(role.id);
                      }
                    }}
                    className="rounded-lg border border-slate-200 hover:bg-rose-50 hover:text-rose-600 px-2.5 py-1.5 text-slate-400"
                    aria-label={`Eliminar rol ${role.name}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "usuarios" && usuarios && roles && (
        <div className="flex-1 min-h-0 overflow-auto bg-white rounded-2xl shadow-soft border border-slate-200/70">
          <div className="px-4 py-2.5 bg-sky-50 border-b border-sky-100 text-xs text-sky-700">
            Los usuarios se dan de alta en <strong>Panel de administración</strong>. Aquí se asignan sus roles.
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Usuario</th>
                <th className="text-left px-4 py-3 font-bold">Roles asignados</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((user) => (
                <tr key={user.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900">{user.nombre}</div>
                    <div className="text-xs text-slate-500">{user.correo}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {roles.map((role) => {
                        const active = user.roles.includes(role.name);
                        return (
                          <button
                            type="button"
                            key={role.id}
                            onClick={() => {
                              const nextRoles = active
                                ? user.roles.filter((name) => name !== role.name)
                                : [...user.roles, role.name];
                              syncUserRolesMut.mutate({ id: user.id, roles: nextRoles });
                            }}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border transition ${
                              active
                                ? "bg-brand-700 text-white border-brand-700"
                                : "bg-white text-slate-500 border-slate-200 hover:border-brand-400"
                            }`}
                          >
                            {active && <Check size={11} />}
                            {role.name}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "permisos" && usuarios && (
        <div className="flex-1 min-h-0 overflow-auto bg-white rounded-2xl shadow-soft border border-slate-200/70">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 text-xs text-amber-800">
            Los permisos directos añaden excepciones a un usuario. No eliminan permisos que ya reciba mediante sus roles.
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Usuario</th>
                <th className="text-left px-4 py-3 font-bold">Roles</th>
                <th className="text-left px-4 py-3 font-bold">Permisos directos</th>
                <th className="text-right px-4 py-3 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((user) => (
                <tr key={user.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900">{user.nombre}</div>
                    <div className="text-xs text-slate-500">{user.correo}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {user.roles.length ? user.roles.join(", ") : "Sin rol"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {user.permisos_directos.length
                      ? `${user.permisos_directos.length} asignados`
                      : "Sin permisos directos"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openDirectPermissions(user)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:border-brand-300 hover:bg-brand-50"
                    >
                      <KeyRound size={15} /> Configurar permisos
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && grupos && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveRole} className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 max-h-[92vh] flex flex-col">
            <ModalHeader
              title={editing ? `Editar rol: ${editing.name}` : "Nuevo rol"}
              onClose={closeRoleModal}
            />

            {error && <ErrorNotice message={error} />}

            <div className="mb-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre del rol *</label>
              <input
                required
                type="text"
                value={nombre}
                onChange={(event) => setNombre(event.target.value)}
                placeholder="ej: comercial_junior"
                className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="flex-1 overflow-auto border border-slate-200 rounded-xl p-1">
              {grupos.map((group) => {
                const allSelected = group.permisos.every((permission) => seleccionados.has(permission.name));
                return (
                  <div key={group.key} className="mb-1 last:mb-0">
                    <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                      <span className="font-bold text-sm text-slate-800">{group.label}</span>
                      <button
                        type="button"
                        onClick={() => toggleRoleGroup(group.permisos.map((permission) => permission.name), allSelected)}
                        className="text-xs font-bold text-brand-700 hover:text-brand-900"
                      >
                        {allSelected ? "Quitar todos" : "Marcar todos"}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 px-2 py-1">
                      {group.permisos.map((permission) => (
                        <label key={permission.name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={seleccionados.has(permission.name)}
                            onChange={() => toggleRolePermission(permission.name)}
                            className="rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                          />
                          <span className="text-slate-700">{permission.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <ModalFooter
              summary={`${seleccionados.size} permisos seleccionados`}
              onCancel={closeRoleModal}
              saving={createMut.isPending || updateMut.isPending}
              saveLabel="Guardar rol"
            />
          </form>
        </div>
      )}

      {permissionUser && grupos && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveDirectPermissions} className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 max-h-[92vh] flex flex-col">
            <ModalHeader title={`Permisos directos: ${permissionUser.nombre}`} onClose={closeDirectPermissions} />

            <div className="mb-4 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-800">
              Los indicadores <strong>Heredado por rol</strong> son informativos. Solo las casillas controlan los permisos directos.
            </div>

            {error && <ErrorNotice message={error} />}

            <div className="flex-1 overflow-auto border border-slate-200 rounded-xl p-1">
              {grupos.map((group) => (
                <div key={group.key} className="mb-1 last:mb-0">
                  <div className="bg-slate-50 px-3 py-2 rounded-lg font-bold text-sm text-slate-800">
                    {group.label}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 px-2 py-1">
                    {group.permisos.map((permission) => {
                      const inherited = permissionUser.permisos_roles.includes(permission.name);
                      const direct = directPermissions.has(permission.name);
                      return (
                        <label key={permission.name} className="flex items-start gap-2 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={direct}
                            onChange={() => toggleDirectPermission(permission.name)}
                            className="mt-0.5 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                          />
                          <span className="min-w-0">
                            <span className="block text-slate-700 font-semibold">{permission.label}</span>
                            <span className="mt-0.5 flex flex-wrap gap-1 text-[11px]">
                              {inherited && (
                                <span className="rounded-full bg-sky-100 px-2 py-0.5 font-bold text-sky-700">Heredado por rol</span>
                              )}
                              {direct && (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-700">Directo</span>
                              )}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <ModalFooter
              summary={`${directPermissions.size} permisos directos seleccionados`}
              onCancel={closeDirectPermissions}
              saving={syncUserPermissionsMut.isPending}
              saveLabel="Guardar permisos"
            />
          </form>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
        active
          ? "bg-brand-700 text-white shadow-soft"
          : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <header className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-black text-slate-900">{title}</h2>
      <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Cerrar">
        <X size={20} />
      </button>
    </header>
  );
}

function ErrorNotice({ message }: { message: string }) {
  return (
    <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
      {message}
    </div>
  );
}

function ModalFooter({
  summary,
  onCancel,
  saving,
  saveLabel,
}: {
  summary: string;
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <span className="text-sm text-slate-500">{summary}</span>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50 disabled:opacity-50">
          Cancelar
        </button>
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

function resolveApiError(error: unknown, fallback: string): string {
  const responseError = error as {
    response?: {
      data?: {
        message?: string;
        errors?: Record<string, string[]>;
      };
    };
  };
  const validation = responseError.response?.data?.errors;
  const first = validation ? Object.values(validation).flat()[0] : null;
  return first ?? responseError.response?.data?.message ?? fallback;
}
